import {
  formatarIdentificacaoObra,
  type ObraIdentificavel,
} from "@/shared/utils/formatar-identificacao-obra";
import { formatDate } from "@/modules/carga-trabalho";
import type { CargaTrabalhoIntervalo } from "@/modules/carga-trabalho";
import type { MarcaSimulacao } from "./simulacao.aplicar";
import type { AlteracaoSimulada } from "./simulacao.model";
import { ROTULO_CAMPO, SIMULACAO_MARCA } from "./simulacao.textos";

/**
 * Conteúdo do tooltip rico do selo "Simulado" (Figma 10892:402, 10892:545, 10892:689): título
 * com a obra, subtítulo com o recurso, campos e rodapé. O Figma só desenha profissional,
 * fornecedor e Local; Papel só existe no remanejamento de profissional (nome pelo `papelObraId` da
 * alocação); sem ele, a linha e o subtítulo ficam só com o nome. Escopo macro não vem na alteração.
 */
export interface CampoTooltip {
  rotulo: string;
  valor: string;
  /** Valor que a simulação substituiu: aparece riscado antes do novo. */
  anterior?: string;
}

export interface DetalheTooltip {
  titulo: string;
  subtitulo?: string;
  campos: CampoTooltip[];
  /** Linhas de texto simples (marca de saída e ações sem desenho no Figma). */
  linhas: string[];
  rodape: string;
}

export const TOOLTIP_RODAPE = "Nada foi gravado ainda. Aplique a simulação para confirmar.";
const RODAPE_FORNECEDOR = "Fornecedor incluído na simulação. Nada foi gravado ainda.";
const RODAPE_LOCAL = "Obra alocada neste local na simulação. Nada foi gravado ainda.";

/** "05/02/2026 – 20/04/2026". */
export function formatarPeriodoTooltip(intervalo: CargaTrabalhoIntervalo): string {
  return `${formatDate(intervalo.inicio)} – ${formatDate(intervalo.fim)}`;
}

/** "Obra 24 - PSV". */
export function tituloObraTooltip(obra: ObraIdentificavel): string {
  return `Obra ${formatarIdentificacaoObra(obra)}`;
}

function daAlteracao(alteracao: AlteracaoSimulada): Omit<DetalheTooltip, "linhas"> | null {
  switch (alteracao.acao) {
    case "remanejar-profissional":
      return {
        titulo: tituloObraTooltip(alteracao.obra),
        subtitulo: [alteracao.novo.nome, alteracao.papel].filter(Boolean).join(" · "),
        campos: [
          {
            rotulo: "Profissional",
            anterior: alteracao.anterior.nome,
            valor: alteracao.novo.nome,
          },
          ...(alteracao.papel ? [{ rotulo: ROTULO_CAMPO.papel, valor: alteracao.papel }] : []),
          { rotulo: "Período", valor: formatarPeriodoTooltip(alteracao.periodo) },
        ],
        rodape: TOOLTIP_RODAPE,
      };
    case "remanejar-fornecedor":
      return {
        titulo: tituloObraTooltip(alteracao.obra),
        subtitulo: alteracao.novo.nome,
        campos: [
          { rotulo: "Fornecedor", anterior: alteracao.anterior.nome, valor: alteracao.novo.nome },
          { rotulo: "Período", valor: formatarPeriodoTooltip(alteracao.periodo) },
        ],
        rodape: RODAPE_FORNECEDOR,
      };
    case "remanejar-local":
      return {
        titulo: tituloObraTooltip(alteracao.obra),
        subtitulo: alteracao.novo.nome,
        campos: [
          { rotulo: "Local", anterior: alteracao.anterior.nome, valor: alteracao.novo.nome },
          { rotulo: "Período", valor: formatarPeriodoTooltip(alteracao.periodo) },
        ],
        rodape: RODAPE_LOCAL,
      };
    default:
      return null;
  }
}

/**
 * Remanejamentos seguem o Figma. Definir equipe, Ignorar conflito e a linha de quem saiu não
 * têm desenho: mostram o texto da marca (valor anterior) no mesmo cartão.
 */
export function detalheTooltip(marca: MarcaSimulacao): DetalheTooltip {
  const campos = marca.alteracao ? daAlteracao(marca.alteracao) : null;
  if (campos) return { ...campos, linhas: [] };
  const [, ...linhas] = marca.texto.split("\n");
  return { titulo: SIMULACAO_MARCA, campos: [], linhas, rodape: TOOLTIP_RODAPE };
}
